/**
 * Quarterly report generator (F17).
 *
 * A printable summary that composes what Phase 10 already built: the pipeline
 * funnel and dwell times, pilot success and spend, the technology mix, source
 * effectiveness and rejection reasons (the analytics aggregate), under a report
 * header naming the quarter and the day it was generated.
 *
 * It is deliberately a COMPOSITION, not a new data source. Reusing
 * `AnalyticsContent` means the printed figures cannot drift from the analytics
 * screen — a report that quietly disagreed with the dashboard it summarises
 * would be worse than no report. The only report-specific logic is the quarter
 * label and the print affordance.
 *
 * Print behaviour: the app chrome carries `print:hidden` at the shell, and the
 * in-page controls (the print button, the back link) are marked `print:hidden`
 * here, so a print shows the report and nothing else. `window.print` is routed
 * through `lib/print` so the button is testable.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 10.3, PRD F17
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Button } from '../../components/ui/Button';
import { useAnalytics } from '../../lib/analytics.queries';
import { formatDate } from '../../lib/format';
import { printPage } from '../../lib/print';
import { ROUTES } from '../../lib/routes';

import { AnalyticsContent } from './AnalyticsPage';
import { quarterLabel } from './quarter';

export function QuarterlyReportPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { data, isPending, isError } = useAnalytics();

  const now = new Date();

  if (isError) {
    return (
      <p role="alert" className="text-sem-bad">
        {t('errors.generic')}
      </p>
    );
  }

  return (
    <>
      <div className="mb-s4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-display text-body text-[10px] font-extrabold tracking-[0.12em] uppercase">
            {t('report.kicker')}
          </p>
          <h1 className="font-display text-navy-text text-2xl font-extrabold">
            {t('report.title', { quarter: quarterLabel(now) })}
          </h1>
          <p className="text-body mt-1 text-sm font-light">
            {t('report.generatedOn', { date: formatDate(now.toISOString()) })}
          </p>
        </div>
        {/* Controls are for the screen, not the page: hidden when printing. */}
        <div className="flex items-center gap-2 print:hidden">
          <Link to={ROUTES.reports} className="text-blue text-sm hover:underline">
            {t('report.backToAnalytics')}
          </Link>
          <Button variant="secondary" size="sm" onClick={printPage}>
            {t('report.print')}
          </Button>
        </div>
      </div>

      {isPending ? (
        <p className="text-body" aria-busy="true">
          {t('state.loading')}
        </p>
      ) : (
        <AnalyticsContent data={data} />
      )}
    </>
  );
}
